import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../../../constants/statusColors';
import {
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  List,
  ListItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  DescriptionOutlined as DescriptionIcon,
  VisibilityOutlined as VisibilityIcon,
  DownloadOutlined as DownloadIcon,
  UploadOutlined as UploadIcon,
  DeleteOutlined as DeleteIcon,
} from '@mui/icons-material';
import DocumentUploadModal from '../../../components/common/DocumentUploadModal';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import { csAdminApi } from '../../../services/api';
import { getSecureDocumentUrl } from '../../../utils/secureFileAccess';
import { useLookups } from '../../../hooks/useLookups';
import {
  DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES,
  lookupLabel,
  partitionDocumentsByMandatory,
  dedupeDocuments,
  normalizeTypeKey,
  resolveDirectorMandatoryTypeCodes,
} from '../../../utils/mandatoryDocumentTypes';

const formatDate = (date) => {
  if (!date) return 'N/A';
  const dt = new Date(date);
  return Number.isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-GB');
};

const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
};

const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) return 'No Date';
  const daysUntil = getDaysUntilExpiry(expiryDate);
  if (daysUntil < 0) return 'Expired';
  if (daysUntil <= 30) return 'Expiring Soon';
  return 'Valid';
};

const getExpiryMessage = (expiryDate) => {
  if (!expiryDate) return null;
  const daysUntil = getDaysUntilExpiry(expiryDate);
  if (daysUntil < 0) return `Expired ${Math.abs(daysUntil)} days ago`;
  if (daysUntil === 0) return 'Expires today';
  if (daysUntil === 1) return 'Expires tomorrow';
  return `Expiring in ${daysUntil} days`;
};

const getExpiryBgColor = (expiryDate) => {
  if (!expiryDate) return '#F5F5F5';
  const daysUntil = getDaysUntilExpiry(expiryDate);
  if (daysUntil < 0) return '#FFEBEE';
  if (daysUntil <= 30) return '#FFF3E0';
  return ACTIVE_GREEN_BG;
};

const getExpiryTextColor = (expiryDate) => {
  if (!expiryDate) return '#757575';
  const daysUntil = getDaysUntilExpiry(expiryDate);
  if (daysUntil < 0) return '#C62828';
  if (daysUntil <= 30) return '#E65100';
  return ACTIVE_GREEN;
};

const formatFileSize = (bytes) => {
  if (!bytes) return null;
  const n = Number(bytes);
  if (Number.isNaN(n)) return bytes;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const resolveFileUrl = (fileUrl) => {
  if (!fileUrl) return null;
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) return fileUrl;
  return `${window.location.origin}${fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`}`;
};

const mapApiDocument = (doc) => ({
  id: doc.id,
  documentId: doc.document_id || doc.id,
  source: doc.source || 'csadmin',
  type: doc.type || doc.category || 'Document',
  typeCode: doc.doc_type || doc.category || doc.typeCode || doc.type,
  typeLabel: doc.type || doc.category,
  name: doc.name || doc.document_name || doc.type,
  fileUrl: doc.fileUrl || doc.file_url,
  expiryDate: doc.expiryDate || doc.expiry_date || null,
  uploadDate: doc.uploadDate || doc.created_at || null,
  size: doc.size || formatFileSize(doc.fileSize || doc.file_size),
  readOnly: doc.readOnly === true || doc.source === 'live',
});

const DocumentsTab = ({ directorId, documents: initialDocuments, companyId, onRefresh, onDocumentsChange }) => {
  const { items: lookupDocumentTypes, loading: lookupsLoading } = useLookups('DIRECTOR_DOCUMENT_TYPE');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadPreset, setUploadPreset] = useState(null);
  const [docs, setDocs] = useState([]);
  const [mandatoryTypeCodes, setMandatoryTypeCodes] = useState([]);
  const [pageReady, setPageReady] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const hasLoadedOnce = useRef(false);

  const loadPageData = useCallback(async ({ silent = false } = {}) => {
    if (lookupsLoading) return;
    if (!directorId) return;

    if (!silent && !hasLoadedOnce.current) {
      setPageReady(false);
    }
    setError('');

    try {
      const requests = [csAdminApi.getDirectorDocuments(directorId)];
      if (companyId) {
        requests.push(csAdminApi.getCompanySettings(companyId));
      }
      const [docsRes, settingsRes] = await Promise.all(requests);
      const documentsSettings = settingsRes?.data?.data?.documents || {};
      setMandatoryTypeCodes(
        companyId
          ? resolveDirectorMandatoryTypeCodes(documentsSettings, lookupDocumentTypes)
          : [...DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES]
      );
      setDocs(dedupeDocuments((docsRes.data?.data || []).map(mapApiDocument)));
    } catch (err) {
      console.error('Director documents API:', err);
      setError('Failed to load documents');
      setMandatoryTypeCodes([...DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES]);
      setDocs((initialDocuments || []).map((doc, idx) => mapApiDocument({ ...doc, id: doc.id || idx })));
    } finally {
      setPageReady(true);
      hasLoadedOnce.current = true;
    }
  }, [directorId, companyId, lookupsLoading, lookupDocumentTypes, onDocumentsChange]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  useEffect(() => {
    if (onDocumentsChange) onDocumentsChange(docs.length);
  }, [docs.length, onDocumentsChange]);

  const { documentSlots, otherDocuments } = useMemo(
    () => partitionDocumentsByMandatory(docs, mandatoryTypeCodes, lookupDocumentTypes),
    [docs, mandatoryTypeCodes, lookupDocumentTypes]
  );

  const handleUploadSuccess = () => {
    loadPageData({ silent: true });
    if (onRefresh) onRefresh();
    setUploadPreset(null);
  };

  const handleOpenUpload = (slot) => {
    setUploadPreset({ label: slot.typeLabel, code: slot.typeCode });
    setUploadModalOpen(true);
  };

  const handleOpenOtherUpload = () => {
    setUploadPreset(null);
    setUploadModalOpen(true);
  };

  const handleView = async (doc) => {
    try {
      const url = await getSecureDocumentUrl(doc.fileUrl);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to open document:', err);
      setError('Failed to open document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const url = await getSecureDocumentUrl(doc.fileUrl);
      if (!url) return;
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name || doc.type || 'document';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to download document:', err);
      setError('Failed to download document');
    }
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const id = documentToDelete?.documentId || documentToDelete?.id;
      if (id && !documentToDelete?.readOnly) {
        await csAdminApi.deleteDocument(id);
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
      setError('Failed to delete document');
    } finally {
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      loadPageData({ silent: true });
      if (onRefresh) onRefresh();
    }
  };

  const renderDocumentActions = (doc) => (
    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
      <IconButton size="small" title="View" onClick={() => handleView(doc)} disabled={!doc.fileUrl}>
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" title="Download" onClick={() => handleDownload(doc)} disabled={!doc.fileUrl}>
        <DownloadIcon fontSize="small" />
      </IconButton>
      {!doc.readOnly && (
        <IconButton
          size="small"
          title="Delete"
          onClick={() => handleDeleteClick(doc)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Director Documents</Typography>
        <Button variant="outlined" startIcon={<UploadIcon />} size="small" onClick={handleOpenOtherUpload}>
          Upload other document
        </Button>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Required types are configured in company Settings → Documents → Mandatory Director Document Types.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!pageReady ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <List sx={{ p: 0, mb: 3 }}>
            {documentSlots.map((slot, index) => (
              <ListItem
                key={slot.typeCode}
                sx={{
                  px: 0,
                  py: 1.5,
                  borderBottom: index < documentSlots.length - 1 ? '1px solid #E0E0E0' : 'none',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                  <DescriptionIcon sx={{ mt: 0.5, color: slot.uploaded ? ACTIVE_GREEN : 'text.disabled' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={500}>{slot.typeLabel}</Typography>
                    {slot.uploaded ? (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {[
                            slot.uploaded.name && normalizeTypeKey(slot.uploaded.name) !== normalizeTypeKey(slot.typeLabel)
                              ? slot.uploaded.name
                              : null,
                            slot.uploaded.expiryDate ? `Expires ${formatDate(slot.uploaded.expiryDate)}` : null,
                          ].filter(Boolean).join(' · ') || 'Uploaded'}
                        </Typography>
                        <Chip
                          label={getExpiryStatus(slot.uploaded.expiryDate)}
                          size="small"
                          sx={{
                            mt: 0.5,
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: getExpiryBgColor(slot.uploaded.expiryDate),
                            color: getExpiryTextColor(slot.uploaded.expiryDate),
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="caption" color="textSecondary">Not uploaded</Typography>
                    )}
                  </Box>
                  {slot.uploaded ? (
                    renderDocumentActions(slot.uploaded)
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => handleOpenUpload(slot)}
                    >
                      Upload
                    </Button>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>

          {otherDocuments.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Other documents</Typography>
              <List sx={{ p: 0 }}>
                {otherDocuments.map((doc, index) => (
                  <ListItem
                    key={doc.documentId || doc.id || `other-${index}`}
                    sx={{
                      px: 0,
                      py: 2,
                      borderBottom: index < otherDocuments.length - 1 ? '1px solid #E0E0E0' : 'none',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <DescriptionIcon sx={{ color: '#E65100' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={500}>
                          {lookupLabel(lookupDocumentTypes, doc.typeCode) || doc.type}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {doc.name}
                          {getExpiryMessage(doc.expiryDate) ? ` · ${getExpiryMessage(doc.expiryDate)}` : ''}
                        </Typography>
                      </Box>
                      {renderDocumentActions(doc)}
                    </Box>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {documentSlots.length === 0 && otherDocuments.length === 0 && (
            <Box textAlign="center" py={4}>
              <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
                No document types configured
              </Typography>
            </Box>
          )}
        </>
      )}

      <DocumentUploadModal
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setUploadPreset(null);
        }}
        onUpload={handleUploadSuccess}
        entityType="director"
        entityId={directorId}
        companyId={companyId}
        presetDocumentType={uploadPreset}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDocumentToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentToDelete?.name || documentToDelete?.type}"?`}
      />
    </Paper>
  );
};

export default DocumentsTab;
